import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useOrg } from "@/contexts/OrgContext";
import { supabase } from "@/integrations/supabase/client";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Building2, Calendar, Handshake, Search, User, UserPlus } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface SearchResult {
  id: string;
  label: string;
  sublabel?: string;
  type: "member" | "prospect" | "company" | "event" | "sponsor";
  route: string;
}

const TYPE_META: Record<SearchResult["type"], { icon: typeof User; color: string; bg: string }> = {
  member: { icon: User, color: "text-blue-600", bg: "bg-blue-50 dark:bg-blue-950" },
  prospect: { icon: UserPlus, color: "text-amber-600", bg: "bg-amber-50 dark:bg-amber-950" },
  company: { icon: Building2, color: "text-purple-600", bg: "bg-purple-50 dark:bg-purple-950" },
  event: { icon: Calendar, color: "text-green-600", bg: "bg-green-50 dark:bg-green-950" },
  sponsor: { icon: Handshake, color: "text-rose-600", bg: "bg-rose-50 dark:bg-rose-950" },
};

const GROUP_LABELS: Record<SearchResult["type"], string> = {
  member: "Members",
  prospect: "Prospects",
  company: "Companies",
  event: "Events",
  sponsor: "Sponsors",
};

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { orgId } = useOrg();

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Prefetch lightweight lists — cached for 5 min, only when dialog open
  const { data: members = [] } = useQuery({
    queryKey: ["global-search-members", orgId],
    enabled: !!orgId && open,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("members")
        .select("id, name, company, membership_status")
        .eq("org_id", orgId)
        .order("name")
        .limit(500);
      return (data || []) as { id: string; name: string; company: string | null; membership_status: string }[];
    },
  });

  const { data: companies = [] } = useQuery({
    queryKey: ["global-search-companies", orgId],
    enabled: !!orgId && open,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("companies")
        .select("id, name, industry")
        .eq("org_id", orgId)
        .order("name")
        .limit(500);
      return (data || []) as { id: string; name: string; industry: string | null }[];
    },
  });

  const { data: events = [] } = useQuery({
    queryKey: ["global-search-events", orgId],
    enabled: !!orgId && open,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("chamber_events")
        .select("id, name, start_date, location")
        .eq("org_id", orgId)
        .order("start_date", { ascending: false })
        .limit(200);
      return (data || []) as { id: string; name: string; start_date: string; location: string }[];
    },
  });

  const { data: sponsors = [] } = useQuery({
    queryKey: ["global-search-sponsors", orgId],
    enabled: !!orgId && open,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("sponsorship_subscriptions")
        .select("id, sponsor_company, status")
        .eq("org_id", orgId)
        .order("sponsor_company")
        .limit(300);
      return (data || []) as { id: string; sponsor_company: string; status: string }[];
    },
  });

  // Build searchable index
  const allResults = useMemo<SearchResult[]>(() => {
    const results: SearchResult[] = [];

    for (const m of members) {
      const isProspect = m.membership_status === "prospect";
      results.push({
        id: m.id,
        label: m.name || "Unnamed",
        sublabel: m.company || undefined,
        type: isProspect ? "prospect" : "member",
        route: isProspect ? `/crm` : `/members/${m.id}`,
      });
    }

    for (const c of companies) {
      results.push({
        id: c.id,
        label: c.name,
        sublabel: c.industry || undefined,
        type: "company",
        route: `/companies?highlight=${c.id}`,
      });
    }

    for (const e of events) {
      results.push({
        id: e.id,
        label: e.name,
        sublabel: e.start_date
          ? new Date(e.start_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
          : e.location,
        type: "event",
        route: `/events?event=${e.id}`,
      });
    }

    for (const s of sponsors) {
      results.push({
        id: s.id,
        label: s.sponsor_company,
        sublabel: s.status,
        type: "sponsor",
        route: `/sponsorship`,
      });
    }

    return results;
  }, [members, companies, events, sponsors]);

  const handleSelect = useCallback(
    (route: string) => {
      setOpen(false);
      navigate(route);
    },
    [navigate],
  );

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Quick Find…</span>
        <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
          ⌘K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search members, prospects, companies, events, sponsors…" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>

          {(["member", "prospect", "company", "event", "sponsor"] as const).map((type) => {
            const items = allResults.filter((r) => r.type === type);
            if (items.length === 0) return null;
            const meta = TYPE_META[type];
            const Icon = meta.icon;

            return (
              <CommandGroup key={type} heading={GROUP_LABELS[type]}>
                {items.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.label} ${item.sublabel || ""}`}
                    onSelect={() => handleSelect(item.route)}
                  >
                    <div className={`mr-2 flex h-6 w-6 items-center justify-center rounded ${meta.bg}`}>
                      <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                    </div>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.sublabel && (
                      <span className="ml-2 text-xs text-muted-foreground truncate max-w-[180px]">{item.sublabel}</span>
                    )}
                    <Badge variant="outline" className="ml-2 text-[10px] capitalize shrink-0">
                      {type}
                    </Badge>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
        </CommandList>
      </CommandDialog>
    </>
  );
}
